import React, { useEffect, useRef, useState } from "react";
import { IconSquareX, IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "../../../library/axios";
import { getSlug } from "../../../config/routes/guards/slug";

// Worker dilayani secara lokal dari /public (tanpa CDN).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// Viewer dokumen "view-only":
// - PDF dirender ke <canvas> (bukan <iframe>), sehingga tidak ada toolbar
//   bawaan browser -> tidak ada tombol Download/Print/Save As.
// - Klik kanan, Ctrl/Cmd+P (Print) dan Ctrl/Cmd+S (Save) diblokir.
// - Saat user memaksa Print, seluruh halaman disembunyikan (@media print).
// - Watermark identitas user ditempel di atas tiap halaman (deteren + jejak).
const Base64ViewerComp = ({ link, close }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(800);
  const [zoom, setZoom] = useState(1);
  const [watermark, setWatermark] = useState("Dokumen Rahasia");
  const [loadError, setLoadError] = useState(false);
  const containerRef = useRef(null);

  // Batas zoom: 50% s/d 300%, langkah 25%.
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;
  const zoomIn = () =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  const zoomOut = () =>
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  const zoomReset = () => setZoom(1);

  // Hitung lebar halaman mengikuti ukuran layar.
  useEffect(() => {
    const calcWidth = () =>
      setPageWidth(Math.min(Math.floor(window.innerWidth * 0.7), 850));
    calcWidth();
    window.addEventListener("resize", calcWidth);
    return () => window.removeEventListener("resize", calcWidth);
  }, []);

  // Ambil identitas user untuk watermark (best-effort, tidak mengganggu jika gagal).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios({
          url: `/users/detailUser`,
          method: "GET",
          headers: {
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${localStorage.getItem(
              `token${getSlug() ? "-" + getSlug() : ""}`
            )}`,
          },
        });
        const nama = data?.data?.namaLengkap;
        if (active && nama) {
          const today = new Date().toLocaleDateString("id-ID");
          setWatermark(`${nama} • ${today}`);
        }
      } catch (err) {
        // abaikan: watermark tetap pakai teks default
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Blokir Print (Ctrl/Cmd+P) & Save (Ctrl/Cmd+S) selama viewer terbuka,
  // serta sembunyikan konten saat dialog print dipaksa muncul.
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (key === "p" || key === "s")) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    window.addEventListener("keydown", onKeyDown, true);

    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-doc-viewer-print-guard", "true");
    styleEl.innerHTML = `@media print { body { display: none !important; } }`;
    document.head.appendChild(styleEl);

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, []);

  return (
    <div
      style={styles.modalOverlay}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="btn-group">
            <button
              className="btn btn-sm btn-secondary"
              onClick={zoomOut}
              disabled={zoom <= ZOOM_MIN}
              title="Perkecil"
              type="button"
            >
              <IconZoomOut stroke={2} />
            </button>
            <button
              className="btn btn-sm btn-light"
              onClick={zoomReset}
              title="Reset zoom"
              type="button"
              style={{ minWidth: "60px" }}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={zoomIn}
              disabled={zoom >= ZOOM_MAX}
              title="Perbesar"
              type="button"
            >
              <IconZoomIn stroke={2} />
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={zoomReset}
              title="Reset zoom"
              type="button"
            >
              <IconZoomReset stroke={2} />
            </button>
          </div>
          <button className="btn btn-sm btn-danger" onClick={close}>
            <IconSquareX stroke={2} />
          </button>
        </div>

        <div ref={containerRef} style={styles.docScroll}>
          <Document
            file={link}
            loading={<div style={styles.message}>Memuat dokumen...</div>}
            error={
              <div style={styles.message}>Gagal memuat dokumen.</div>
            }
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              setLoadError(false);
            }}
            onLoadError={() => setLoadError(true)}
          >
            {!loadError &&
              Array.from({ length: numPages }, (_, i) => (
                <div key={i} style={styles.pageWrapper}>
                  <Page
                    pageNumber={i + 1}
                    width={Math.round(pageWidth * zoom)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  {/* Watermark overlay per halaman */}
                  <div style={styles.watermarkOverlay} aria-hidden="true">
                    <div style={styles.watermarkText}>
                      {Array.from({ length: 60 }, () => watermark).join("   ")}
                    </div>
                  </div>
                </div>
              ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

// Inline styles
const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    width: "85%",
    maxHeight: "90%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
  },
  docScroll: {
    overflow: "auto",
    maxHeight: "75vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    backgroundColor: "#525659",
    padding: "16px",
    borderRadius: "4px",
  },
  pageWrapper: {
    position: "relative",
    boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
  },
  watermarkOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  watermarkText: {
    transform: "rotate(-30deg)",
    color: "rgba(120, 120, 120, 0.18)",
    fontSize: "26px",
    fontWeight: 700,
    lineHeight: "70px",
    wordSpacing: "10px",
    textAlign: "center",
    whiteSpace: "normal",
    width: "150%",
  },
  message: {
    color: "#fff",
    padding: "40px",
    textAlign: "center",
  },
};

export default Base64ViewerComp;
